import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Tool } from '@/shared/new-pic-editor.component';

export interface ModelOption {
  value: string;
  label: string;
  vision: boolean;
  promptPrice: number;
}

// Import default prompts (Assuming they are exported correctly from where they will be defined/fetched)
// TODO: Define these constants properly, perhaps in a separate file or fetch from backend initially.
const DEFAULT_IMPROVE_PROMPT_BEDEK = `
אתה מהנדס בניין מומחה לבדיקות ליקויי בנייה בדירות ומבנים,. תפקידך הוא לנתח מידע שסופק (טקסט, תמונות, נתונים טכניים) ולייצר פסקאות דו"ח בדיקה מקצועיות, ברורות, ותמציתיות עבור כל ליקוי, בהתאם לדפוסים מקצועיים מקובלים בתחום בדק הבית בישראל.

*הנחיות ליבה:*
1.  *שפה:* עברית בלבד, תקנית, רשמית ומקצועית.
2.  *סגנון:* ישיר, מדויק, חד-משמעי. השתמש במשפטים קצרים וברורים. הפורמט חייב להיות נקי ואסתטי.
3.  *מבנה קבוע לכל ליקוי:* הפלט חייב להיות במבנה הבא, תוך שמירה קפדנית על שורה ריקה בין הפסקאות:
*ליקוי*: [תיאור ברור ומדויק של הבעיה, כולל תקן אם יש]
*נדרש:* [מה הפעולה המדויקת לתיקון – כולל תנאים אסתטיים אם רלוונטי]
*מחיר:* [הערכת עלות לתיקון בש"ח, רצוי בטווח (לדוגמה: 600–1,500 ש"ח). אם לא ניתן לספק הערכה מדויקת (למשל, תלוי בבדיקות נוספות), ציין זאת במפורש והסבר מדוע (לדוגמה: העלות תיקבע לאחר ביצוע בדיקת מעבדה, או ללא עלות בשלב זה). אל תשאיר סעיף זה ריק.]
4.  *דיוק וודאות:* נסח קביעות ברורות. הימנע לחלוטין מניסוחים מעורפלים ("נראה ש...", "ייתכן", "חשד ל...")
5.  *טיפול בליקויים אסתטיים:* כאשר מדובר בנזק אסתטי (שריטות, פגמים בציפוי, הבדלי גוון), הצג בבירור בסעיף 'נדרש' את *חלופות התיקון* (תיקון מקומי מול החלפה/צביעה מלאה) וציין *השלכות אפשריות* (כגון הבדלי גוון או טקסטורה לאחר תיקון נקודתי).
6.  *פעולות מותנות ובדיקות נוספות:* אם הפעולה הנדרשת או העלות תלויות בתוצאות של בדיקה ספציפית (כמו איתור נזילות, בדיקת מעבדה, בדיקת חוזק הדבקה), *ציין זאת בבירור* בסעיף 'נדרש' או 'מחיר'.
7.  *הדגשת בטיחות:* במקרים רלוונטיים (מעקות רופפים, חשמל לא תקין, ליקויי ממ"ד, פינות חדות), ציין בקצרה את *ההיבט הבטיחותי* של הליקוי.
8.  *ניתוח תמונות:* אם סופקו תמונות, נתח אותן במקצועיות. תאר בקצרה את הממצא החזותי הרלוונטי וקשר אותו לתיאור הליקוי ולפעולה הנדרשת.
9.  *טיפול במיקום:* ציין את מיקום הליקוי באופן ברור ככל הניתן. אם המידע חסר אך קריטי, ציין שיש למסור מיקום מדויק.
10. *טיפול בקלט ברוסית:* הבן קלט בשפה הרוסית, אך הגב תמיד בעברית בלבד.
11. *תמציתיות:* כל סעיף (ליקוי/נדרש/מחיר) לא יעלה על 1-2 משפטים. הפלט הכולל לא יעלה על 5-6 שורות. אל תוסיף הקדמות, סיכומים, הערות אישיות, שאלות או הסברים מעבר לנדרש.

*דוגמה לסגנון ומבנה נכון:*

*ליקוי:* גימור טיח וצבע במראה לא אחיד.
ניכרים סמני מברשת/רולר בולטים, גבשושי וחוסר אחידות בגוון הצבע.
*נדרש:*
א. לבצע שכבת צבע נוספת עד לקבלת גוון אחיד, ללא סימני גרגירים וללא סימני מברשת או רולר.
ב. להקפיד לפרק את גופי החשמל בטרם הצביעה ולהרכיבם באופן מדויק ויציב לאחר גמרה צביעה."

*ליקוי:* אותרה שריטה בזכוכית הוויטרינה, אשר פוגעת באסתטיקה של הדירה ועלולה להחמיר עם הזמן.
*נדרש:* לבדוק אפשרות ללטש את השריטה במידה והיא שטחית. אם לא ניתן להסיר את השריטה על ידי ליטוש, יש לשקול החלפת הזכוכית על מנת להחזיר את המראה האסתטי.

*ליקוי:*  אותרו סימני נזק (שריטות ו/או פגמים/או קילופים) ע"ג המעקה מרפסת.
*נדרש:* ללטש את המעקה ולתקן את השריטות בצורה תקינה ואסתטית באישורת המזמין.. במידה והתיקון לא יהיה אסתטי, יש לשקול החלפת האלמנט הפגום.

*ליקוי:* שריטות/קילוף צבע ע"ג פרופיל מוצר האלומיניום חלון.
*נדרש:* תיקון פחחות ו/או צביעה אסתטית בשיטת התזה באמצעות ספריי אירוסול בגוון התואם למניפת של הצבעים היצרן.

*ליקוי:* קיימים סדקים בטיח.
*נדרש:*
1. פתיחת הסדקים באמצעות כלי מתאים.
2. מילוי הסדקים בחומר איטום מתאים ושכבת שפכטל.
3. שיוף וצביעה בגוון תואם לקיר.

*ליקוי:* אבני חיפוי סף החלון (קופינג) בוצעו ללא קיבוע בברגים.
*נדרש:* עיגון אבני קופינג בברגים.

*ליקוי:* לא בוצע מישק הפרדה במפגש שיש (נדבך ראש של מעקה מרפסת) וחיפוי הקיר.
*נדרש:* יש למלא בצורה תקינה (בעובי של 4 מ"מ לפחות) ומקצועית את מישקי האבן למניעת חדירת רטיבות.

*ליקוי:* מישקי התפשטות מלאים בחומר קשיח (רובה) במקום חומר גמיש (מסטיק).
מדובר בפרט המועד לפורענות מחמת סדיקה וניתוק מילוי הרובה עקב מאמצים פנימיים הנוצרים בתוך מערכת האריחים.
*נדרש:* יש לחרוץ את הרובה הקיימת לרוחב ולפתוח מישק ברוחב לפחות 4-6 ס"מ ולבצע מילוי חומר איטום גמיש.
`;

const DEFAULT_IMPROVE_PROMPT_RETIVUT = `מטרתך היא לשפר את הטקסט שאני מספק לך, העוסק באיתור נזילות ורטיבות, על פי הכללים הבאים:

- שמור במדויק על המשמעות המקורית של הטקסט.
- השתמש בעברית תקנית, מקצועית וישירה.
- דייק במונחים המקצועיים המקובלים בתחום האינסטלציה ואיתור הנזילות.
- תקן שגיאות לשוניות ושפר את מבנה המשפטים.

**חריג:** אם מופיעה המילה "נדרש:" בטקסט המקורי – מותר לך להוסיף המלצות או פעולות מעשיות באופן עצמאי, בהתאם למקובל והמקצועי בתחום איתור הנזילות. אם המילה "נדרש:" לא מופיעה בטקסט המקורי – אל תוסיף המלצות או פעולות מעבר למה שכתבתי, רק שפר לשונית וסגנונית.

**דוגמה:**

*מקורי:* קיימת נזילה מתחת לכיור במטבח.

*משופר (ללא "נדרש:")* קיימת נזילה מתחת לכיור המטבח.

*מקורי עם "נדרש:":* קיימת נזילה מתחת לכיור במטבח. נדרש:

*משופר (עם "נדרש:")* קיימת נזילה מתחת לכיור המטבח. נדרש: יש לפרק את הסיפון ולבדוק אטמים וצינורות ניקוז, ובמידת הצורך להחליף את הרכיבים הפגומים ולהתקין מחדש.

**כללים מחייבים ליישום על כל קלט עתידי:**

- הגב תמיד בעברית בלבד.
- שמור על ניסוח קצר ותמציתי ככל הניתן.
- אל תוסיף שום מידע נוסף, הערות אישיות, שאלות, הסברים מעבר לנדרש, המלצות כלליות או הצעות. הפלט יכלול אך ורק את התיאור המקצועי הממוקד בסגנון שהוגדר.
- עבד כל קלט חדש שיתקבל בהתאם להנחיות אלו, תוך יישום הסגנון והטרמינולוגיה שהודגמו.
`;

// TODO: Add default spell check prompts
const DEFAULT_SPELLCHECK_PROMPT_BEDEK = `תקן את שגיאות הכתיב והדקדוק בטקסט הבא הקשור לבדק בית, תוך שמירה על המשמעות המקורית והמונחים המקצועיים. אל תשנה את תוכן הטקסט, רק תקן שגיאות.`;
const DEFAULT_SPELLCHECK_PROMPT_RETIVUT = `תקן את שגיאות הכתיב והדקדוק בטקסט הבא הקשור לאיתור נזילות, תוך שמירה על המשמעות המקורית והמונחים המקצועיים. אל תשנה את תוכן הטקסט, רק תקן שגיאות.`;

const STORAGE_NAME = 'settings';
@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private config = {
    hebrewOnly: true,
    defaultShape: 'rectangle' as Tool,
    enableSeparateDefectTypes: false,
    visualModel: 'google/gemini-3-flash-preview',
    textAiModel: 'openai/gpt-5.4-mini',
    improveTextPromptBedek: DEFAULT_IMPROVE_PROMPT_BEDEK,
    improveTextPromptRetivut: DEFAULT_IMPROVE_PROMPT_RETIVUT,
    spellCheckPromptBedek: DEFAULT_SPELLCHECK_PROMPT_BEDEK,
    spellCheckPromptRetivut: DEFAULT_SPELLCHECK_PROMPT_RETIVUT,
    classifyImagesImmediately: true,
    enablePlaceGatedDecisions: true,
    enableInitialDecisions: true,
    enableMemorySafeImages: this.isIOS(), // Default ON for iOS, OFF for others
  };

  private isIOS(): boolean {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  get hebrewOnly() {
    return this.config.hebrewOnly;
  }

  set hebrewOnly(val: boolean) {
    this.config.hebrewOnly = val;
    this.save();
  }

  get defaultShape(): Tool {
    return this.config.defaultShape;
  }

  set defaultShape(val: Tool) {
    this.config.defaultShape = val;
    this.save();
  }

  get enableSeparateDefectTypes() {
    return this.config.enableSeparateDefectTypes;
  }

  set enableSeparateDefectTypes(val: boolean) {
    this.config.enableSeparateDefectTypes = val;
    this.save();
  }

  get visualModel() {
    return this.config.visualModel;
  }

  set visualModel(val: string) {
    this.config.visualModel = val;
    this.save();
  }

  get textAiModel() {
    return this.config.textAiModel;
  }

  set textAiModel(val: string) {
    this.config.textAiModel = val;
    this.save();
  }

  get improveTextPromptBedek(): string {
    return this.config.improveTextPromptBedek || DEFAULT_IMPROVE_PROMPT_BEDEK;
  }

  set improveTextPromptBedek(val: string) {
    this.config.improveTextPromptBedek = val;
    this.save();
  }

  get improveTextPromptRetivut(): string {
    return this.config.improveTextPromptRetivut || DEFAULT_IMPROVE_PROMPT_RETIVUT;
  }

  set improveTextPromptRetivut(val: string) {
    this.config.improveTextPromptRetivut = val;
    this.save();
  }

  get spellCheckPromptBedek(): string {
    return this.config.spellCheckPromptBedek || DEFAULT_SPELLCHECK_PROMPT_BEDEK;
  }

  set spellCheckPromptBedek(val: string) {
    this.config.spellCheckPromptBedek = val;
    this.save();
  }

  get spellCheckPromptRetivut(): string {
    return this.config.spellCheckPromptRetivut || DEFAULT_SPELLCHECK_PROMPT_RETIVUT;
  }

  set spellCheckPromptRetivut(val: string) {
    this.config.spellCheckPromptRetivut = val;
    this.save();
  }

  get classifyImagesImmediately() {
    return this.config.classifyImagesImmediately;
  }

  set classifyImagesImmediately(val: boolean) {
    this.config.classifyImagesImmediately = val;
    this.save();
  }

  get enablePlaceGatedDecisions() {
    return this.config.enablePlaceGatedDecisions !== false;
  }

  set enablePlaceGatedDecisions(val: boolean) {
    this.config.enablePlaceGatedDecisions = val;
    this.save();
  }

  get enableInitialDecisions() {
    return this.config.enableInitialDecisions !== false;
  }

  set enableInitialDecisions(val: boolean) {
    this.config.enableInitialDecisions = val;
    this.save();
  }

  get enableMemorySafeImages() {
    return this.config.enableMemorySafeImages;
  }

  set enableMemorySafeImages(val: boolean) {
    this.config.enableMemorySafeImages = val;
    this.save();
  }

  /**
   * Get upload concurrency limit based on memory-safe mode and platform
   * iOS: 3 concurrent uploads (WKWebView memory constraints)
   * Others: 5 concurrent uploads
   */
  get uploadConcurrencyLimit(): number {
    if (this.config.enableMemorySafeImages) {
      return this.isIOS() ? 3 : 4;
    }
    return 5;
  }

  // Add static getters for default prompts to be used in the modal for reverting
  static get defaultImprovePromptBedek(): string {
    return DEFAULT_IMPROVE_PROMPT_BEDEK;
  }

  static get defaultImprovePromptRetivut(): string {
    return DEFAULT_IMPROVE_PROMPT_RETIVUT;
  }

  static get defaultSpellCheckPromptBedek(): string {
    return DEFAULT_SPELLCHECK_PROMPT_BEDEK;
  }

  static get defaultSpellCheckPromptRetivut(): string {
    return DEFAULT_SPELLCHECK_PROMPT_RETIVUT;
  }

  visualModels: ModelOption[] = [];
  textModels: ModelOption[] = [];
  modelsLoaded = false;

  constructor(
    private storageService: Storage,
    private functions: Functions,
  ) {
    this.initStorageAndLoadConfig();
    this.fetchModels();
  }

  private async fetchModels() {
    try {
      const getModels = httpsCallable<void, { visualModels: ModelOption[]; textModels: ModelOption[] }>(
        this.functions, 'getAvailableModels',
      );
      const result = await getModels();
      this.visualModels = result.data.visualModels;
      this.textModels = result.data.textModels;
    } catch (e) {
      console.warn('[SettingsService] Failed to fetch models:', e);
    } finally {
      this.modelsLoaded = true;
    }
  }

  private async initStorageAndLoadConfig() {
    const storage = await this.storageService.create();
    const savedConfig = await storage.get(STORAGE_NAME);

    if (savedConfig) {
      const parsedConfig = JSON.parse(savedConfig);
      if (
        !parsedConfig.defaultShape ||
        !['rectangle', 'arrow', 'circle', 'select'].includes(parsedConfig.defaultShape)
      ) {
        parsedConfig.defaultShape = 'rectangle';
      }
      // Remap missing or dead classify visual models. Do not force other
      // live selections (including gemini-3.5-flash) onto gpt-5.4-mini.
      if (!parsedConfig.visualModel || parsedConfig.visualModel === 'openai/gpt-5.4-mini') {
        parsedConfig.visualModel = 'google/gemini-3-flash-preview';
      }
      if (!parsedConfig.textAiModel || parsedConfig.textAiModel === 'openai/gpt-5.6-luna') {
        parsedConfig.textAiModel = 'openai/gpt-5.4-mini';
      }
      this.config = { ...this.config, ...parsedConfig };
      // Ensure prompts have default values if missing from storage
      this.config.improveTextPromptBedek = this.config.improveTextPromptBedek || DEFAULT_IMPROVE_PROMPT_BEDEK;
      this.config.improveTextPromptRetivut = this.config.improveTextPromptRetivut || DEFAULT_IMPROVE_PROMPT_RETIVUT;
      this.config.spellCheckPromptBedek = this.config.spellCheckPromptBedek || DEFAULT_SPELLCHECK_PROMPT_BEDEK;
      this.config.spellCheckPromptRetivut = this.config.spellCheckPromptRetivut || DEFAULT_SPELLCHECK_PROMPT_RETIVUT;
    } else {
      storage.set(STORAGE_NAME, JSON.stringify(this.config));
    }
    await this.save();
  }

  private async save() {
    const storage = await this.storageService.create();
    return storage.set(STORAGE_NAME, JSON.stringify(this.config));
  }
}
